import { SortTypeEnum } from "@repo/helpers";
import { Column, DateColumn, GridPreLoaded } from "@repo/ui";
import { useMemo } from "react";

import type { ApplicationSetting } from "../../../models";
import type { RowStatus, Segment, SettingGridRow } from "../settingsTree";

import { isCompanyWide, rowStatus } from "../settingsTree";
import { SettingRowActions } from "./settingRowActions";
import style from "./style.module.scss";
import { ValueText } from "./valueText";

interface Props {
	row: SettingGridRow;
	segment?: Segment;
	onEdit?: (setting: ApplicationSetting) => void;
	onDelete?: (setting: ApplicationSetting) => void;
}

interface SegmentRow extends ApplicationSetting {
	status: RowStatus;
}

const statuses: Record<RowStatus, { label: string; className: string; title: string }> = {
	used: { label: "Used", className: style.setBadge, title: "This row's value is the one shown" },
	outranked: {
		label: "Outranked",
		className: style.defaultBadge,
		title: "Applies to the selected segment, but a more specific row wins",
	},
	otherSegment: {
		label: "Other segment",
		className: style.otherBadge,
		title: "Doesn't apply to the selected segment",
	},
};

const statusOrder: RowStatus[] = ["used", "outranked", "otherSegment"];

const segmentColumns = [
	new Column<SegmentRow>("Brand", "brand"),
	new Column<SegmentRow>("Channel", "channel"),
	new Column<SegmentRow>("Marketer", "marketer"),
	new Column<SegmentRow>("State", "usState"),
	new Column<SegmentRow>("Office", "branch"),
];

const statusColumn = new Column<SegmentRow>("Status", "status").withFormat((status: RowStatus) => (
	<span className={statuses[status].className} title={statuses[status].title}>
		{statuses[status].label}
	</span>
));

const trailingColumns = [
	new DateColumn<SegmentRow>("Updated", "updatedOn"),
	new Column<SegmentRow>("Revision", "revision"),
	new Column<SegmentRow>("Notes", "notes").withCharLimit(100),
];

/** Every row for one setting name, with the row that applies to the segment first */
export function SegmentRowsGrid({ row, segment, onEdit, onDelete }: Props) {
	// Shows the segment columns anyway when a company-wide setting still has a segment row, which the code never reads
	const hideSegments = row.isCompanyWide && row.rows.every(isCompanyWide);
	const gridColumns = useMemo(() => {
		// ValueText cuts long values off itself: the grid's own char limit would turn the element into text
		const valueColumn = new Column<SegmentRow>("Value", "value")
			.withFullObject()
			.withFormat((setting: SegmentRow) => (
				<ValueText
					value={setting.value}
					enumOptions={row.enumOptions}
					charLimit={100}
					masked={setting.isMasked}
				/>
			));
		// A company-wide setting's rows have no segment to show
		const shown = [statusColumn, valueColumn, ...(hideSegments ? [] : segmentColumns), ...trailingColumns];
		if (!onEdit || !onDelete) return shown;
		const actions = new Column<SegmentRow>("", undefined, SortTypeEnum.none, undefined, false)
			.withId("actions")
			.withFormat((setting: SegmentRow) => (
				<SettingRowActions setting={setting} onEdit={onEdit} onDelete={onDelete} />
			));
		return [...shown, actions];
	}, [hideSegments, onEdit, onDelete, row.enumOptions]);

	// A company-wide setting is read without a segment, so none of its rows applies to the selected one
	const appliedSegment = row.isCompanyWide ? undefined : segment;
	const values = useMemo(
		() =>
			row.rows
				.map((setting): SegmentRow => ({ ...setting, status: rowStatus(setting, row.winner, appliedSegment) }))
				.toSorted(
					(a, b) =>
						statusOrder.indexOf(a.status) - statusOrder.indexOf(b.status) ||
						b.segmentPriority - a.segmentPriority,
				),
		[row, appliedSegment],
	);

	return (
		<GridPreLoaded<SegmentRow>
			// The grid keeps the columns it first rendered, so start a new one when the segment columns come or go
			key={hideSegments ? "companyWide" : "segmented"}
			id={`appSettingRows_${row.key}`}
			keyProperty="applicationSettingId"
			columns={gridColumns}
			values={values}
			hidePagination
		/>
	);
}
